<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header">
        <span class="hris-page-eyebrow">Timekeeping</span>
        <h1 class="hris-page-title">Holidays</h1>
        <p class="hris-page-subtitle">Manage company holidays and review Philippine public holidays by year.</p>
      </div>

      <q-form ref="vForm" greedy @submit.prevent="onSubmit">
        <div class="holidays-layout">
          <div class="hris-form-section holidays-form">
            <h2 class="hris-form-section-title">Add Holiday</h2>
            <div class="hris-form-row">
              <div class="hris-form-field">
                <label class="hris-form-label">Holiday</label>
                <q-input
                  v-model="holidayname"
                  outlined
                  dense
                  color="green"
                  :rules="holidayFormRules.name"
                  class="caps"
                ></q-input>
              </div>
            </div>
            <div class="hris-form-row">
              <div class="hris-form-field">
                <label class="hris-form-label">Date</label>
                <q-input
                  v-model="holidaydate"
                  mask="date"
                  outlined
                  dense
                  color="green"
                  :rules="holidayFormRules.date"
                >
                  <template v-slot:append>
                    <q-icon name="far fa-calendar-alt" class="cursor-pointer">
                      <q-popup-proxy
                        ref="qDateProxy"
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-date
                          v-model="holidaydate"
                          @input="() => $refs.qDateProxy.hide()"
                        ></q-date>
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                </q-input>
              </div>
            </div>
            <div class="hris-form-row">
              <div class="hris-form-field">
                <label class="hris-form-label">Location</label>
                <q-input
                  v-model="locationText"
                  outlined
                  dense
                  color="green"
                  autogrow
                  type="textarea"
                  rows="2"
                  :rules="locationFieldRules"
                ></q-input>
              </div>
            </div>

            <div class="hris-form-actions" style="justify-content: stretch;">
              <q-btn unelevated class="hris-q-primary full-width" type="submit" label="Save"></q-btn>
            </div>
          </div>
          <div class="hris-panel holidays-list">
            <div class="row items-center q-gutter-md q-mb-sm">
              <q-select
                v-model="selectedYear"
                :options="years"
                label="Year"
                emit-value
                map-options
                dense
                outlined
                color="green"
                style="min-width: 140px"
                class="col-auto"
                @input="changeYear"
              ></q-select>
              <q-spinner
                v-if="loadingPublic || loadingCompany"
                color="green"
                size="sm"
              ></q-spinner>
            </div>
            <q-table
              title="Holidays List"
              :data="mergedHolidays"
              :columns="columns"
              row-key="rowKeyId"
              :pagination.sync="pagination"
              flat
            >
              <template v-slot:body="props">
                <q-tr
                  slot="body"
                  :props="props"
                  @click="selectedRow(props)"
                  style="cursor: pointer"
                >
                  <q-td key="holiday" :props="props">{{
                    props.row.holiday
                  }}</q-td>
                  <q-td key="holiday_date" :props="props">{{
                    formatHolidayDate(props.row.holiday_date)
                  }}</q-td>
                  <q-td key="holiday_type" :props="props">
                    <div
                      v-if="props.row.rowSource === 'public_ph'"
                      class="column items-center q-gutter-xs"
                    >
                      <template v-if="props.row.phLaborCategory === 'regular'">
                        <span class="hris-badge hris-badge-success">Regular</span>
                      </template>
                      <template
                        v-else-if="props.row.phLaborCategory === 'special'"
                      >
                        <span class="hris-badge hris-badge-accent">Special</span>
                      </template>
                      <template v-else>
                        <span class="hris-badge hris-badge-success">Regular</span>
                      </template>
                    </div>
                    <span
                      v-else
                      class="hris-badge"
                      :class="props.row.holiday_type === 'regular' ? 'hris-badge-success' : 'hris-badge-accent'"
                    >
                      {{ String(props.row.holiday_type).toUpperCase() }}
                    </span>
                  </q-td>
                  <q-td key="locations" :props="props">{{
                    props.row.locations
                  }}</q-td>
                  <q-td key="action" :props="props">
                    <div v-if="props.row.rowSource === 'company'">
                      <q-btn
                        round
                        outline
                        color="red"
                        @click.stop="removeRow(props)"
                      >
                        <i class="fas fa-trash"></i>
                        <q-tooltip
                          anchor="top middle"
                          self="bottom middle"
                          :offset="[10, 10]"
                          color="red"
                        >
                          <strong>Remove (all years)</strong>
                        </q-tooltip>
                      </q-btn>
                    </div>
                    <span v-else class="text-caption text-grey-6"
                      >Reference</span
                    >
                  </q-td>
                </q-tr>
              </template>
              <template v-slot:no-data>
                <div class="hris-empty">
                  <span>
                    No holidays loaded for {{ selectedYear }} yet
                    <span v-if="publicLoadError || companyLoadError">
                      (check connection or HRIS).</span
                    >
                  </span>
                </div>
              </template>
            </q-table>
          </div>
        </div>
      </q-form>
    </div>

    <q-dialog v-model="spinner" persistent>
      <div class="hris-loading" style="background: transparent; min-width: 120px;">
        <q-spinner color="green" size="48px"></q-spinner>
      </div>
    </q-dialog>

    <q-dialog v-model="deleted" persistent>
      <div class="hris-modal">
        <div class="hris-modal-body">
          <p style="margin: 0;">
            This will permanently delete this special holiday for
            <strong>every year</strong> it was replicated (same name, locations,
            and month/day). Continue?
          </p>
        </div>
        <div class="hris-modal-footer">
          <q-btn unelevated class="hris-q-outline" label="Cancel" v-close-popup></q-btn>
          <q-btn
            unelevated
            class="hris-q-primary"
            label="Continue"
            v-close-popup
            @click="deletionConfirmation"
          ></q-btn>
        </div>
      </div>
    </q-dialog>
  </div>
</template>

<script>
import axios from "axios";
import vueCookies from "vue-cookies";
import {
  PH_HOLIDAY_LABOR_GUIDE,
  classifyPhilippineNationalHolidayName,
} from "../../constants/philippineHolidayLaborLaw";

const NAGER_BASE = "https://date.nager.at/api/v3/PublicHolidays";

export default {
  data() {
    const cy = new Date().getFullYear();
    const yearOpts = [];
    for (let y = cy - 5; y <= cy + 5; y++) {
      yearOpts.push({ label: String(y), value: y });
    }
    return {
      tempID: 0,
      spinner: false,
      deleted: false,
      holidayname: "",
      holidaydate: "",
      /** Manual form always saves HRIS holidays as special (non-working) */
      holidaytype: "special",
      selectedYear: cy,
      /** saved in HRIS */
      companyHolidays: [],
      publicHolidays: [],
      fetchSeq: 0,
      loadingPublic: false,
      loadingCompany: false,
      publicLoadError: false,
      companyLoadError: false,
      columns: [
        {
          name: "holiday",
          align: "center",
          label: "Event",
          field: "holiday",
          sortable: true,
        },
        {
          name: "holiday_date",
          align: "center",
          label: "Date",
          field: "holiday_date",
          sortable: true,
        },
        {
          name: "holiday_type",
          align: "center",
          label: "Types",
          field: "holiday_type",
          sortable: true,
        },
        {
          name: "locations",
          align: "center",
          label: "Locations",
          field: "locations",
          sortable: true,
        },
        {
          name: "action",
          align: "center",
          label: "Action",
          field: "",
          sortable: false,
        },
      ],
      years: yearOpts,
      pagination: {
        rowsPerPage: 20,
      },
      /** Free text — required for every manual holiday (comma-separated codes) */
      locationText: "",
      timeZone: null,
      phHolidayGuide: PH_HOLIDAY_LABOR_GUIDE,
    };
  },

  computed: {
    holidayFormRules() {
      const fr = this.formRules;
      return {
        name: [fr.required],
        date: [fr.required, "date"],
      };
    },

    /** Required for every manual save (special-only flow) */
    locationFieldRules() {
      return [
        (val) =>
          !!(val != null && String(val).trim()) || "Location is required",
      ];
    },

    replicaYearRangeText() {
      const cy = new Date().getFullYear();
      return `${cy - 5} to ${cy + 5}`;
    },

    mergedHolidays() {
      const companyRows = (this.companyHolidays || []).map((r) => ({
        ...r,
        rowSource: "company",
        rowKeyId: String(r.id),
        locations:
          r.locations != null && String(r.locations).length ? r.locations : "—",
      }));

      const publicRows = (this.publicHolidays || []).map((r, idx) => {
        const phTypes = Array.isArray(r.types) ? r.types.join(", ") : "";
        const typeField = phTypes.replace(/_/g, " ") || "Public";
        const phLaborCategory = classifyPhilippineNationalHolidayName(
          r.holiday
        );
        return {
          id: null,
          holiday: r.holiday,
          holiday_date: normalizeDateKey(r.holiday_date),
          holiday_type: typeField,
          publicTypesLabel: phTypes.replace(/_/g, " ") || "",
          phLaborCategory,
          locations: "Philippines",
          rowSource: "public_ph",
          rowKeyId: `ph-${r.holiday_date}-${idx}`,
        };
      });

      const combined = [...companyRows, ...publicRows];
      combined.sort((a, b) => {
        const da = String(a.holiday_date || "");
        const db = String(b.holiday_date || "");
        const c = da.localeCompare(db);
        if (c !== 0) {
          return c;
        }
        return String(a.holiday).localeCompare(String(b.holiday));
      });
      return combined;
    },
  },

  created() {
    this.loadCompanyHolidays();
    this.fetchPublicHolidayList();
    this.getUserTimeZone();
  },

  methods: {
    formatHolidayDate(raw) {
      const key = normalizeDateKey(raw);
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(key);
      if (!m) {
        return raw;
      }
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    async onSubmit() {
      let valid = true;
      const form = this.$refs.vForm;
      if (form && typeof form.validate === "function") {
        const res = form.validate();
        valid =
          res && typeof res.then === "function" ? await res : Boolean(res);
      }
      if (!valid) {
        this.$q.notify({
          type: "warning",
          message: "Fill in all required fields before saving.",
          position: "center",
          timeout: 2500,
        });
        return;
      }

      this.spinner = true;
      let formData = new FormData();

      formData.append("tempID", this.tempID);
      formData.append("holiday", this.holidayname);
      formData.append("holiday_date", this.holidaydate);
      formData.append("holiday_type", "special");
      formData.append("location", (this.locationText || "").trim());
      axios
        .create({
          baseURL: this.apiUrl,
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .post("/timekeeping/holiday", formData)
        .then((res) => {
          const wasNew = this.tempID === 0;
          const d = res.data || {};
          this.holidayname = "";
          this.holidaydate = "";
          this.holidaytype = "special";
          this.locationText = "";
          this.tempID = 0;
          this.$nextTick(() => {
            if (this.$refs.vForm && this.$refs.vForm.resetValidation) {
              this.$refs.vForm.resetValidation();
            }
          });
          this.loadCompanyHolidays();
          let message = wasNew
            ? "Successfully saved!"
            : "Successfully updated!";
          if (wasNew && typeof d.replicated === "number") {
            const skip =
              typeof d.skippedDuplicates === "number" ? d.skippedDuplicates : 0;
            message = `Saved for ${d.replicated} year(s)`;
            if (skip > 0) {
              message += ` (${skip} duplicate year(s) skipped)`;
            }
            message += ".";
          }
          this.$q.notify({
            message,
            color: "green",
            position: "center",
            actions: [
              {
                label: "Dismiss",
                color: "white",
                handler: () => {
                  /* ... */
                },
              },
            ],
          });
          this.spinner = false;
        })
        .catch(() => {
          this.spinner = false;
        });
    },

    loadCompanyHolidays: function () {
      this.loadingCompany = true;
      this.companyLoadError = false;
      const year = Number(this.selectedYear);
      axios
        .create({
          baseURL: this.apiUrl,
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        })
        .get(`/timekeeping/holiday/${year}`)
        .then((res) => {
          const list = Array.isArray(res.data && res.data.data)
            ? res.data.data
            : [];
          this.companyHolidays = list;
        })
        .catch(() => {
          this.companyHolidays = [];
          this.companyLoadError = true;
        })
        .finally(() => {
          this.loadingCompany = false;
          setTimeout(() => {
            this.spinner = false;
          }, 300);
        });
    },

    async fetchPublicHolidayList() {
      const year = Number(this.selectedYear);
      this.fetchSeq += 1;
      const seq = this.fetchSeq;
      this.loadingPublic = true;
      this.publicLoadError = false;
      try {
        const res = await axios.get(`${NAGER_BASE}/${year}/PH`);
        if (seq !== this.fetchSeq) {
          return;
        }
        const rows = Array.isArray(res.data) ? res.data : [];
        this.publicHolidays = rows.map((h) => ({
          holiday:
            h.name && String(h.name).trim() ? h.name : h.localName || "Holiday",
          holiday_date: h.date,
          types: h.types || [],
        }));
      } catch (e) {
        if (seq !== this.fetchSeq) {
          return;
        }
        this.publicHolidays = [];
        this.publicLoadError = true;
        this.$q.notify({
          type: "negative",
          message:
            "Could not load Philippines public holidays (date.nager.at).",
        });
      } finally {
        if (seq === this.fetchSeq) {
          this.loadingPublic = false;
        }
      }
    },

    selectedRow: function (obj) {
      if (obj.row.rowSource !== "company") {
        return;
      }
      this.holidayname = obj.row.holiday;
      this.holidaytype = "special";
      this.holidaydate = obj.row.holiday_date;
      this.tempID = obj.row.id;
      const loc =
        obj.row.locations != null && obj.row.locations !== "—"
          ? String(obj.row.locations)
          : "";
      this.locationText = loc.replace(/\s*,\s*/g, ", ").trim();
    },

    changeYear: function () {
      this.loadCompanyHolidays();
      this.fetchPublicHolidayList();
    },

    removeRow: function (obj) {
      if (obj.row.rowSource !== "company") {
        return;
      }
      this.deleted = true;
      this.tempID = obj.row.id;
    },

    deletionConfirmation: function () {
      this.spinner = true;
      let formData = new FormData();
      formData.append("tempID", this.tempID);
      axios
        .create({
          baseURL: this.apiUrl,
          headers: { Authorization: `Bearer ${this.token}` },
        })
        .post("/timekeeping/holiday/delete", formData)
        .then((res) => {
          this.tempID = 0;
          this.loadCompanyHolidays();
          const n =
            res.data && typeof res.data.deletedCount === "number"
              ? res.data.deletedCount
              : null;
          this.$q.notify({
            message:
              n != null
                ? `Removed ${n} matching holiday row(s) across years.`
                : "Holiday deleted.",
            color: "positive",
            position: "center",
          });
          this.spinner = false;
        })
        .catch(() => {
          this.spinner = false;
        });
    },

    getUserTimeZone() {
      const s = vueCookies.get("sgcchris");
      this.timeZone = s && s.user ? s.user.time_zone : null;
    },
  },
};

function normalizeDateKey(raw) {
  if (raw == null) {
    return "";
  }
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }
  if (/^\d{4}\/\d{2}\/\d{2}/.test(s)) {
    return s.replace(/\//g, "-");
  }
  return s;
}
</script>

<style scoped>
.holidays-layout {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-start;
}

.holidays-form {
  flex: 1 1 300px;
  max-width: 340px;
}

.holidays-list {
  flex: 2 1 480px;
  min-width: 0;
}

@media (max-width: 900px) {
  .holidays-form {
    max-width: 100%;
  }
}
</style>
